import { friendlyDbError } from '../utils/friendlyDbError';
import { create } from 'zustand';
import { supabase } from '../supabase';
import { getCurrentUserOrganizationId } from './referenceDataCache';
import type { LeadSource } from '../types';

interface LeadSourceStore {
  leadSources: LeadSource[];
  loading: boolean;
  error: string | null;
  fetchLeadSources: () => Promise<void>;
  createLeadSource: (source: Omit<LeadSource, 'id' | 'organization_id' | 'created_at' | 'updated_at'>) => Promise<LeadSource | null>;
  updateLeadSource: (id: string, updates: Partial<LeadSource>) => Promise<LeadSource | null>;
  deleteLeadSource: (id: string) => Promise<boolean>;
  getLeadSourcesByCategory: (category?: string) => LeadSource[];
}

export const useLeadSourceStore = create<LeadSourceStore>((set, get) => ({
  leadSources: [],
  loading: false,
  error: null,

  fetchLeadSources: async () => {
    set({ loading: true, error: null });
    try {
      // Get current user's organization ID from cache
      const organizationId = await getCurrentUserOrganizationId();
      if (!organizationId) {
        throw new Error('Organization ID not found for current user');
      }

      const { data, error } = await supabase
        .from('lead_sources')
        .select('*')
        .eq('organization_id', organizationId)
        .eq('is_active', true)
        .order('sort_order', { ascending: true });

      if (error) throw error;
      set({ leadSources: data || [] });
    } catch (error) {
      set({ error: friendlyDbError(error) });
    } finally {
      set({ loading: false });
    }
  },

  createLeadSource: async (source) => {
    try {
      // Get current user's organization_id
      const { data: userData, error: userError } = await supabase.auth.getUser();
      
      if (userError) throw userError;
      
      // Get user profile to get organization_id
      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();
      
      if (profileError) throw profileError;
      
      if (!profileData?.organization_id) {
        throw new Error('Organization ID not found for current user');
      }

      const { data, error } = await supabase
        .from('lead_sources')
        .insert({
          ...source,
          organization_id: profileData.organization_id
        })
        .select()
        .single();

      if (error) throw error;
      
      const leadSources = get().leadSources;
      set({ leadSources: [...leadSources, data] });
      return data;
    } catch (error) {
      set({ error: friendlyDbError(error) });
      return null;
    }
  },

  updateLeadSource: async (id, updates) => {
    try {
      const { data, error } = await supabase
        .from('lead_sources')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      const leadSources = get().leadSources.map(source => 
        source.id === id ? { ...source, ...data } : source
      );
      set({ leadSources });
      return data;
    } catch (error) {
      set({ error: friendlyDbError(error) });
      return null;
    }
  },

  deleteLeadSource: async (id) => {
    try {
      const { error } = await supabase
        .from('lead_sources')
        .delete()
        .eq('id', id);

      if (error) throw error;

      const leadSources = get().leadSources.filter(source => source.id !== id);
      set({ leadSources });
      return true;
    } catch (error) {
      set({ error: friendlyDbError(error) });
      return false;
    }
  },

  getLeadSourcesByCategory: (category?: string) => {
    const { leadSources } = get();
    if (!category) return leadSources;
    return leadSources.filter(source => source.category === category);
  }
}));